import React, { FC, useEffect, useState } from 'react';
import ChevronArrowRightReactIcon from 'assets/react-icons/ChevronArrowRightReactIcon';
import { DropDownChevronDownIcon } from 'assets/icons';
import CustomPopover from 'components/custom-popover/CustomPopover';
import classNames from 'classnames';

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  limit: number;
  wrapperClassName?: string;
  showSelectResultsPerPage?: boolean;
  onSelectResultsPerPage?: (pageSize: number) => void;
}

const Pagination: FC<PaginationProps> = ({
  currentPage,
  onPageChange,
  totalCount,
  limit,
  wrapperClassName = 'fixed bottom-0 w-full',
  showSelectResultsPerPage = false,
  onSelectResultsPerPage
}) => {
  const [intermediatePagesList, setIntermediatePagesList] = useState<number[]>([]);
  const [showLimitOptions, setShowLimitOptions] = useState(false);

  const totalPages = Math.ceil(totalCount / limit);
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = currentPage * limit > totalCount ? totalCount : currentPage * limit;
  const showLeftArrow = currentPage > 1;
  const showRightArrow = currentPage < totalPages;
  const showStartingElipsis = intermediatePagesList[0] > 2;
  const showEndingElipsis = intermediatePagesList[intermediatePagesList.length - 1] < totalPages - 1;

  useEffect(() => {
    if (totalPages <= 6) {
      const pagesList = [...Array(totalPages).keys()]
        .filter(i => {
          return i !== 0 && i !== totalPages - 1;
        })
        .map(i => i + 1);
      setIntermediatePagesList(pagesList);
    } else {
      const prevPage = currentPage - 1;
      const nextPage = currentPage + 1;
      if (currentPage === 1) setIntermediatePagesList([2, 3]);
      else if (currentPage === totalPages) setIntermediatePagesList([prevPage - 1, prevPage]);
      else if (prevPage === 1) setIntermediatePagesList([currentPage, nextPage]);
      else if (nextPage === totalPages) setIntermediatePagesList([prevPage, currentPage]);
      else setIntermediatePagesList([prevPage, currentPage, nextPage]);
    }
  }, [currentPage, totalPages]);

  const handleLimitChange = (pageSize: number) => {
    if (onSelectResultsPerPage) {
      if (pageSize === limit) {
        setShowLimitOptions(false);
        return;
      }
      onSelectResultsPerPage(pageSize);
    }
    setShowLimitOptions(false);
  };

  // eslint-disable-next-line react/no-multi-comp
  const PageTile = ({ page }: { page: number }) => {
    return (
      <div
        className={`flex h-8 w-8 cursor-pointer items-center justify-center text-sbase font-semibold
      text-primaryText
        ${currentPage === page ? 'rounded-full bg-secondaryBtn font-extrabold text-secondary' : ' bg-white '}`}
        role="presentation"
        onClick={() => onPageChange(page)}>
        {page}
      </div>
    );
  };

  return (
    <div
      id="pagination"
      className={classNames(
        'flex h-20 items-center justify-between border-t border-t-secondaryBtn bg-white px-[30px]',
        wrapperClassName
      )}>
      <div className="text-sbase font-semibold text-accentText">
        {`Showing ${startIndex} to ${endIndex} of ${totalCount} results`}
      </div>
      <div className="flex gap-x-4 bg-white ">
        {showLeftArrow && (
          <div
            className="flex h-10 w-10 cursor-pointer items-center justify-center bg-white"
            onClick={() => onPageChange(currentPage - 1)}
            role="presentation">
            <ChevronArrowRightReactIcon id="pagination-left-arrow" fill="#ACB5BD" className="shrink-0 rotate-180" />
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-x-4">
            <PageTile page={1} />
            {showStartingElipsis && <div className="text-sbase font-semibold text-primaryText">...</div>}
            {intermediatePagesList.length > 0 && intermediatePagesList.map(page => <PageTile key={page} page={page} />)}
            {showEndingElipsis && <div className="text-sbase font-semibold text-primaryText">...</div>}
            <PageTile page={totalPages} />
          </div>
        )}
        <div className="flex h-10 w-10 items-center justify-center bg-white">
          {showRightArrow && (
            <ChevronArrowRightReactIcon
              id="pagination-right-arrow"
              className="shrink-0 cursor-pointer"
              fill="#ACB5BD"
              onClick={() => onPageChange(currentPage + 1)}
              role="presentation"
            />
          )}
        </div>
      </div>
      <div className={showSelectResultsPerPage ? '' : 'w-40'}>
        {showSelectResultsPerPage && (
          <div className="flex items-center gap-x-2">
            <span className="text-sbase font-semibold text-accentText">Results</span>
            <CustomPopover
              displayAs="contents"
              showOnTop={true}
              anchorComponent={({ isPanelOpen }) => (
                <div className="flex cursor-pointer items-center gap-x-1 text-sbase font-semibold">
                  <span className="text-secondary">{limit} per page</span>
                  <DropDownChevronDownIcon
                    className={`path-fill-current shrink-0 text-secondary transition-transform  ${
                      isPanelOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              )}
              show={showLimitOptions}
              onTogglePopover={() => setShowLimitOptions(!showLimitOptions)}
              onClosePopover={() => setShowLimitOptions(false)}
              offset="-right-2"
              margin="mb-2"
              width="w-max max-w-[120px]"
              minWidth="min-w-max"
              padding="p-2">
              <div className="flex flex-col gap-y-2">
                {[10, 20, 50, 100, 200].map(pageSize => (
                  <div
                    key={pageSize}
                    className={`cursor-pointer rounded px-2 py-1 text-sbase hover:bg-secondaryBtn ${
                      limit === pageSize ? 'bg-secondaryBtn font-semibold text-secondary' : 'text-primaryText'
                    }`}
                    onClick={() => handleLimitChange(pageSize)}>
                    {pageSize} per page
                  </div>
                ))}
              </div>
            </CustomPopover>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
